import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { TailSpin } from "react-loader-spinner";
import { BiExport } from "react-icons/bi";

import AdminTransactionsExportModal from "../../components/admin-export/AdminTransactionsExportModal";
import { DataTable } from "../../components/data-table";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { PROD_LINK, STAGING_LINK } from "../../constant";
import { IS_STAGING } from "../../constant/api";
import type { AdminBillPaymentSubtypeEntry } from "../../lib/api/adminBillPaymentSummaries";
import { useAdminBillPaymentSummariesQuery } from "../../lib/api/useAdminBillPaymentSummariesQuery";
import type { AdminBillPaymentTransaction } from "../../lib/api/adminTransactionsData";
import { useAdminBillPaymentTransactionsDataQuery } from "../../lib/api/useAdminBillPaymentTransactionsDataQuery";
import { formatAmount } from "../../utils/FormatPerson";
import {
  BILL_PAYMENT_SUBTYPE_FILTERS,
  BILL_PAYMENT_TABLE_LIMIT,
  type BillPaymentSubtypeFilterValue,
  buildBillPaymentTableRows,
  createBillPaymentTableColumns,
  type BillPaymentTableRow,
} from "./billPaymentTransactionsTable";
import AdminBillPaymentDetailModal from "./AdminBillPaymentDetailModal";

function titleFromKey(key: string): string {
  if (key === "undefined") return "Unknown";
  const spaced = key.replace(/([a-z])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function statusLabel(status: string): string {
  return status === "undefined" ? "Unknown" : status;
}

function subtypeRows(
  record: Record<string, AdminBillPaymentSubtypeEntry> | undefined
): { key: string; label: string; entry: AdminBillPaymentSubtypeEntry }[] {
  if (!record || typeof record !== "object") return [];
  return Object.entries(record)
    .map(([key, entry]) => ({
      key,
      label: titleFromKey(key),
      entry,
    }))
    .sort((a, b) => b.entry.count - a.entry.count);
}

const BillPaymentsOverview: React.FC = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [billTablePage, setBillTablePage] = useState(1);
  const [billTableSearch, setBillTableSearch] = useState("");
  const [billSubtypeFilter, setBillSubtypeFilter] =
    useState<BillPaymentSubtypeFilterValue>("airtime");
  const [billDetailOpen, setBillDetailOpen] = useState(false);
  const [billDetailTx, setBillDetailTx] =
    useState<AdminBillPaymentTransaction | null>(null);
  const [exportOpen, setExportOpen] = useState(false);

  const dateRangeInvalid = Boolean(startDate && endDate) && startDate > endDate;

  const queryDates = useMemo(() => {
    if (startDate && endDate && !dateRangeInvalid) {
      return { startDate, endDate };
    }
    return {};
  }, [startDate, endDate, dateRangeInvalid]);

  const { data, isPending, isError, error, refetch, isFetching } =
    useAdminBillPaymentSummariesQuery({
      ...queryDates,
      enabled: !dateRangeInvalid,
    });

  const {
    data: billTxPayload,
    isPending: billTxPending,
    isError: billTxError,
    error: billTxQueryError,
    refetch: refetchBillTx,
    isFetching: billTxFetching,
  } = useAdminBillPaymentTransactionsDataQuery({
    page: billTablePage,
    search: billTableSearch,
    limit: BILL_PAYMENT_TABLE_LIMIT,
    ...queryDates,
    billSubtypes: billSubtypeFilter,
    enabled: !dateRangeInvalid,
  });

  const billTransactions = useMemo(
    () => billTxPayload?.transactions ?? [],
    [billTxPayload?.transactions]
  );
  const billPagination = billTxPayload?.pagination;

  const billTableRows = useMemo(
    () => buildBillPaymentTableRows(billTransactions),
    [billTransactions]
  );

  const openBillDetail = useCallback((row: BillPaymentTableRow) => {
    setBillDetailTx(row.raw);
    setBillDetailOpen(true);
  }, []);

  const billColumns = useMemo(
    () => createBillPaymentTableColumns(openBillDetail),
    [openBillDetail]
  );

  const billTableTotalPages = Math.max(1, billPagination?.totalPages ?? 1);
  const billTableTotalCount = billPagination?.totalCount ?? 0;

  useEffect(() => {
    setBillTablePage(1);
  }, [
    startDate,
    endDate,
    dateRangeInvalid,
    billSubtypeFilter,
    billTableSearch,
  ]);

  const statusEntries = useMemo(() => {
    if (!data?.statusBreakdown) return [];
    return Object.entries(data.statusBreakdown).sort((a, b) => b[1] - a[1]);
  }, [data?.statusBreakdown]);

  const subtypeTable = useMemo(
    () => subtypeRows(data?.subtypeBreakdown),
    [data?.subtypeBreakdown]
  );

  return (
    <>
      <div className="flex h-screen w-full flex-col overflow-hidden">
        <div className="flex h-[31px] w-full shrink-0 items-center justify-center bg-[#1B1B1B]">
          {IS_STAGING ? (
            <h4 className="text-[12px] font-medium text-[#AEBCC6]">
              You are in Staging:{" "}
              <span>
                <a href={PROD_LINK} className="text-[#03C668]">
                  Switch to Prod
                </a>
              </span>
            </h4>
          ) : (
            <h4 className="text-[12px] font-medium text-[#AEBCC6]">
              You are in PROD:{" "}
              <span>
                <a href={STAGING_LINK} className="text-[#DF4E4E]">
                  Switch to Staging
                </a>
              </span>
            </h4>
          )}
        </div>
        <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden lgss:flex-row">
          <Sidebar />
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto bg-[#13181B] lgss:w-10/12">
            <Navbar />
            <div className="mx-auto w-full max-w-[1600px] flex-1 bg-[#13181B] px-5 pb-24 pt-6 font-manrope mds:px-8 lgss:px-10">
              <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <h1 className="font-sora text-xl font-semibold tracking-tight text-white">
                    Bill payments
                  </h1>
                  <p className="mt-1 max-w-xl text-[13px] leading-relaxed text-[#869AA9]">
                    Airtime, data, electricity, and other bill-payment activity.
                    Summaries use the admin transactions endpoint with type{" "}
                    <span className="font-mono text-[#BEE1FB]">
                      billpayment
                    </span>
                    . Leave dates empty for all-time totals.
                  </p>
                </div>

                <div
                  className="w-full rounded-xl border border-[#2C363E] bg-[#161E25]/80 p-4 shadow-[0_4px_24px_rgba(0,0,0,0.25)] lg:w-auto lg:min-w-[320px]"
                  role="group"
                  aria-label="Date range filter"
                >
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-[#869AA9]">
                    Date range
                  </p>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <div className="min-w-0 flex-1">
                      <label
                        htmlFor="billpay-summary-start"
                        className="mb-1 block text-[11px] text-[#5C6B76]"
                      >
                        From
                      </label>
                      <input
                        id="billpay-summary-start"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#1C2227] px-3 text-[13px] text-[#E7EBEE] shadow-inner outline-none transition focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <label
                        htmlFor="billpay-summary-end"
                        className="mb-1 block text-[11px] text-[#5C6B76]"
                      >
                        To
                      </label>
                      <input
                        id="billpay-summary-end"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#1C2227] px-3 text-[13px] text-[#E7EBEE] shadow-inner outline-none transition focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setStartDate("");
                        setEndDate("");
                      }}
                      className="h-10 shrink-0 rounded-lg border border-[#2C363E] px-4 text-[12px] font-semibold text-[#AEBCC6] transition hover:border-[#3A4852] hover:bg-[#273037] hover:text-white active:scale-[0.98]"
                    >
                      Reset
                    </button>
                  </div>
                  {(startDate || endDate) && !(startDate && endDate) && (
                    <p className="mt-2 text-[11px] text-[#5C6B76]">
                      Select both dates to filter; otherwise all-time data is
                      shown.
                    </p>
                  )}
                </div>
              </header>

              {dateRangeInvalid && (
                <div
                  className="mb-6 rounded-xl border border-amber-500/35 bg-amber-500/[0.07] px-5 py-8 text-center"
                  role="alert"
                >
                  <p className="text-[14px] font-semibold text-amber-200/95">
                    Invalid date range
                  </p>
                  <p className="mx-auto mt-2 max-w-md text-[13px] leading-relaxed text-[#AEBCC6]">
                    Choose an end date on or after the start date, or reset the
                    filter.
                  </p>
                </div>
              )}

              {!dateRangeInvalid && isPending && (
                <div
                  className="flex flex-col items-center justify-center gap-4 rounded-xl border border-[#2C363E] bg-[#161E25]/40 py-24"
                  aria-busy="true"
                >
                  <TailSpin color="#0B75C2" height={40} width={40} />
                  <p className="text-[13px] text-[#869AA9]">
                    Loading summaries…
                  </p>
                </div>
              )}

              {!dateRangeInvalid && isError && (
                <div className="flex flex-col gap-3 rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-4 text-[13px] text-red-100/95 sm:flex-row sm:items-center sm:justify-between">
                  <span>
                    {error?.response?.data &&
                    typeof error.response.data === "object" &&
                    "message" in error.response.data
                      ? String(
                          (error.response.data as { message?: string }).message
                        )
                      : "Could not load bill payment summaries."}
                  </span>
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="shrink-0 rounded-lg border border-red-500/40 bg-red-500/20 px-4 py-2 text-[12px] font-semibold text-red-200 transition hover:bg-red-500/30"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!dateRangeInvalid && !isPending && !isError && data && (
                <>
                  {isFetching && !isPending && (
                    <div className="mb-4 flex items-center gap-2 text-[12px] text-[#869AA9]">
                      <span
                        className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-[#869AA9] border-t-transparent"
                        aria-hidden
                      />
                      Refreshing…
                    </div>
                  )}

                  <section
                    className="mb-6 rounded-2xl border border-[#2C363E] bg-gradient-to-b from-[#1C2227] to-[#181D22] p-5 shadow-[0_8px_32px_rgba(0,0,0,0.28)] sm:p-7"
                    aria-labelledby="billpay-totals-heading"
                  >
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className="rounded-full border border-[#3A4852] bg-[#0D1114]/80 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#AEBCC6]">
                        USDT
                      </span>
                    </div>
                    <p
                      id="billpay-totals-heading"
                      className="text-[11px] font-bold uppercase tracking-widest text-[#869AA9]"
                    >
                      Volume
                    </p>
                    <div className="mt-4">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#617889]">
                        Total (USDT)
                      </p>
                      <p className="mt-1 font-sora text-2xl font-bold tabular-nums text-[#BEE1FB] sm:text-3xl">
                        {formatAmount(data.totalAmountUSDT)} USDT
                      </p>
                    </div>
                    <p className="mt-4 text-[13px] text-[#869AA9]">
                      <span className="font-semibold text-[#E7EBEE]">
                        {data.totalCount.toLocaleString()}
                      </span>{" "}
                      bill payment transactions in this range
                    </p>
                  </section>

                  {statusEntries.length > 0 && (
                    <section className="mb-6 rounded-xl border border-[#2C363E] bg-[#161E25]/50 p-4 sm:p-5">
                      <h3 className="mb-3 text-[12px] font-semibold text-[#E7EBEE]">
                        Status breakdown
                      </h3>
                      <ul className="flex flex-wrap gap-2">
                        {statusEntries.map(([status, count]) => (
                          <li
                            key={status}
                            className="inline-flex items-center gap-2 rounded-lg border border-[#2C363E] bg-[#0D1114]/60 px-3 py-2"
                          >
                            <span className="text-[11px] font-bold uppercase tracking-wide text-[#AEBCC6]">
                              {statusLabel(status)}
                            </span>
                            <span className="text-[14px] font-semibold tabular-nums text-white">
                              {count.toLocaleString()}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  <section className="overflow-hidden rounded-xl border border-[#2C363E] bg-[#161E25]/40">
                    <div className="border-b border-[#2C363E] bg-[#1C2227]/80 px-4 py-3">
                      <h3 className="text-[13px] font-bold text-white">
                        By bill subtype
                      </h3>
                      <p className="mt-0.5 text-[11px] text-[#5C6B76]">
                        Counts, NGN / USDT amounts, and per-subtype status mix
                      </p>
                    </div>
                    {subtypeTable.length === 0 ? (
                      <p className="px-4 py-10 text-center text-[13px] text-[#869AA9]">
                        No subtype breakdown for this range.
                      </p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px] text-left text-[13px]">
                          <thead className="bg-[#0D1114]/80 text-[11px] font-semibold uppercase tracking-wide text-[#869AA9]">
                            <tr>
                              <th className="px-4 py-2.5">Subtype</th>
                              <th className="px-4 py-2.5 text-right tabular-nums">
                                Count
                              </th>
                              <th className="px-4 py-2.5 text-right tabular-nums">
                                NGN
                              </th>
                              <th className="px-4 py-2.5 text-right tabular-nums">
                                USDT
                              </th>
                              <th className="min-w-[200px] px-4 py-2.5">
                                Status mix
                              </th>
                            </tr>
                          </thead>
                          <tbody className="text-[#E7EBEE]">
                            {subtypeTable.map(({ key, label, entry }) => {
                              const subStatus = Object.entries(
                                entry.statusBreakdown
                              ).sort((a, b) => b[1] - a[1]);
                              return (
                                <tr
                                  key={key}
                                  className="border-t border-[#2C363E] bg-[#13181B]/40 align-top"
                                >
                                  <td className="px-4 py-3 font-medium text-white">
                                    {label}
                                  </td>
                                  <td className="px-4 py-3 text-right tabular-nums">
                                    {entry.count.toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3 text-right tabular-nums text-[#BEE1FB]">
                                    ₦{formatAmount(entry.amountNGN)}
                                  </td>
                                  <td className="px-4 py-3 text-right tabular-nums text-[#A8D4F0]">
                                    {formatAmount(entry.amountUSDT)}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex flex-wrap gap-1.5">
                                      {subStatus.map(([st, n]) => (
                                        <span
                                          key={st}
                                          className="inline-flex items-center gap-1 rounded-md border border-[#2C363E] bg-[#0D1114]/80 px-2 py-0.5 text-[11px]"
                                        >
                                          <span className="font-semibold uppercase text-[#7D8F9C]">
                                            {statusLabel(st)}
                                          </span>
                                          <span className="tabular-nums text-white">
                                            {n}
                                          </span>
                                        </span>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>
                </>
              )}

              {!dateRangeInvalid && (
                <>
                  <div className="w-full flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mt-4 mb-3">
                    <h4 className="text-white font-sora font-semibold text-[12px] shrink-0">
                      Activity
                    </h4>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      <button
                        type="button"
                        onClick={() => setExportOpen(true)}
                        className="flex items-center justify-center gap-2 cursor-pointer opacity-90 hover:opacity-100"
                        aria-label="Export summaries and bill payments"
                      >
                        <BiExport className="text-white text-lg" aria-hidden />
                        <span className="uppercase font-sora font-semibold text-[#869AA9] text-[10px] mt-0.5">
                          Export
                        </span>
                      </button>
                    </div>
                  </div>
                  <section
                    id="bill-payment-transactions"
                    className="mt-10 border-t border-[#2C363E]/80 pt-10"
                  >
                    <div className="mb-4">
                      <h3 className="font-sora text-[15px] font-bold text-white">
                        Bill payment transactions
                      </h3>
                    </div>

                    <div
                      className="mb-4 flex flex-wrap gap-2"
                      role="tablist"
                      aria-label="Bill payment subtype filter"
                    >
                      {BILL_PAYMENT_SUBTYPE_FILTERS.map((opt) => {
                        const active = billSubtypeFilter === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => setBillSubtypeFilter(opt.value)}
                            className={
                              active
                                ? "rounded-full border border-secondary_button bg-secondary_button/15 px-4 py-2 text-[12px] font-bold text-secondary_button"
                                : "rounded-full border border-[#2C363E] bg-[#161E25]/80 px-4 py-2 text-[12px] font-semibold text-[#AEBCC6] transition hover:border-[#3A4852] hover:text-white"
                            }
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mb-4">
                      <input
                        type="search"
                        value={billTableSearch}
                        onChange={(e) => setBillTableSearch(e.target.value)}
                        placeholder="Search transactions"
                        className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#161E25]/80 px-3 text-[13px] text-[#E7EBEE] outline-none transition focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
                      />
                    </div>

                    {billTxFetching && !billTxPending && (
                      <p className="mb-2 text-[12px] text-[#869AA9]">
                        Updating list…
                      </p>
                    )}

                    {billTxError && (
                      <div className="mb-4 flex flex-col gap-2 rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-3 text-[13px] text-red-100/95 sm:flex-row sm:items-center sm:justify-between">
                        <span>
                          {axios.isAxiosError(billTxQueryError) &&
                          billTxQueryError.response?.data &&
                          typeof billTxQueryError.response.data === "object" &&
                          "message" in billTxQueryError.response.data
                            ? String(
                                (
                                  billTxQueryError.response.data as {
                                    message?: string;
                                  }
                                ).message
                              )
                            : "Could not load bill payment transactions."}
                        </span>
                        <button
                          type="button"
                          onClick={() => void refetchBillTx()}
                          className="shrink-0 rounded-lg border border-red-500/40 bg-red-500/20 px-3 py-1.5 text-[12px] font-semibold text-red-200"
                        >
                          Retry
                        </button>
                      </div>
                    )}

                    {billTxPending && !billTxError && (
                      <div className="flex justify-center rounded-xl border border-[#2C363E] bg-[#161E25]/40 py-16">
                        <TailSpin color="#0B75C2" height={36} width={36} />
                      </div>
                    )}

                    {!billTxPending && !billTxError && (
                      <DataTable<BillPaymentTableRow>
                        columns={billColumns}
                        data={billTableRows}
                        currentPage={billTablePage}
                        setCurrentPage={setBillTablePage}
                        totalPage={billTableTotalPages}
                        count={billTableTotalCount}
                        limit={BILL_PAYMENT_TABLE_LIMIT}
                        emptyContent="No bill payment transactions for this filter."
                        className="overflow-hidden rounded-xl border border-[#2C363E]"
                        fixedBodyHeight="min(520px, 48vh)"
                      />
                    )}
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <AdminBillPaymentDetailModal
        open={billDetailOpen}
        onClose={() => {
          setBillDetailOpen(false);
          setBillDetailTx(null);
        }}
        transaction={billDetailTx}
      />
      <AdminTransactionsExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Bill payments"
        exportContext={{
          type: "billpayment",
          billSubtypes: billSubtypeFilter,
        }}
      />
    </>
  );
};

export default BillPaymentsOverview;
